import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {RequestQueryPayload} from '@cureskin/api-client';
import {ChatService} from '@services/chat-service/chat-service';
import {ChatChannel, ChatMessage} from '@services/chat-service/chat-service.types';
import {ConnectionService} from '@services/connection-service';
import {AppConfig} from '../../app/app.config';

const PROGRESS_CHECK_FACE_FINAL_OK = 'ProgressCheckFaceFinalOk';
const SMS_COPY = 'SMS_COPY';

export interface BotReplyBackoff {
  t1: number;
  t2: number;
}

// Provided locally by each consumer alongside ChatService so state is isolated
// from any other chat thread (e.g. the treatment-session chat in
// UserQueryChatBottomsheet). Do NOT add `providedIn: 'root'`.
@Injectable()
export class FollowUpChatService {
  static readonly COMPLETION_QUESTION_ID = PROGRESS_CHECK_FACE_FINAL_OK;
  static readonly DEFAULT_BACKOFF: BotReplyBackoff = { t1: 1000, t2: 1000 };

  private channel: ChatChannel | null = null;

  constructor(
    private readonly chatService: ChatService,
    private readonly connectionService: ConnectionService,
    private readonly appConfig: AppConfig,
  ) {}

  configureForFollowUp(user: Table.User, followUp: Table.FollowUp): void {
    this.channel = {
      user,
      parent: followUp,
      parentField: 'followUp',
      fetchChats: async (payload: RequestQueryPayload<Table.FollowUpChat>): Promise<Table.FollowUpChat[]> => {
        return await this.connectionService.findFollowUpChat(payload);
      },
    };
  }

  hasChannel(): boolean {
    return !!this.channel;
  }

  get allChats$(): Observable<ChatMessage[]> {
    return this.chatService.allChats$;
  }

  get latestMessage$(): Observable<ChatMessage | null> {
    return this.chatService.latestMessage$;
  }

  get showThinking$(): Observable<boolean> {
    return this.chatService.showThinking$;
  }

  get loadingOldMessages$(): Observable<boolean> {
    return this.chatService.loadingOldMessages$;
  }

  get currentMessages(): ChatMessage[] {
    return this.chatService.currentMessages;
  }

  setThinking(value: boolean): void {
    this.chatService.setThinking(value);
  }

  async loadChatHistory(reset: boolean = true): Promise<ChatMessage[]> {
    return this.chatService.loadChatHistory(this.requireChannel(), reset);
  }

  async loadMoreMessages(): Promise<boolean> {
    return this.chatService.loadMoreMessages(this.requireChannel());
  }

  async sendMessage(lastMessage: Table.FollowUpChat, userReply: string): Promise<Table.FollowUpChat> {
    return this.chatService.sendMessage(this.requireChannel(), lastMessage, userReply);
  }

  createReplyMessage(lastMessage: Table.FollowUpChat, userReply: string): Table.FollowUpChat {
    return this.chatService.createReplyMessage(this.requireChannel(), lastMessage, userReply);
  }

  addLocalMessages(messages: Table.FollowUpChat[]): void {
    this.chatService.addLocalMessages(messages);
  }

  popLastMessage(): void {
    this.chatService.popLastMessage();
  }

  stopBotReplyTimer(): void {
    this.chatService.stopBotReplyTimer();
  }

  isCompletionMessage(message: Table.FollowUpChat | undefined): boolean {
    return message?.get?.('questionId') === PROGRESS_CHECK_FACE_FINAL_OK;
  }

  getLatestActionableMessage(messages: Table.FollowUpChat[]): Table.FollowUpChat | undefined {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const message = messages[i];
      if (message?.get?.('Mode') === SMS_COPY) continue;
      return message;
    }
    return undefined;
  }

  countAnsweredQuestions(messages: Table.FollowUpChat[]): number {
    const ownerUser = this.appConfig.Shared.Assistant.Owner.USER;
    return messages.filter((message): boolean => message?.get?.('Owner') === ownerUser).length;
  }

  nextBackoff(current: BotReplyBackoff): BotReplyBackoff {
    return { t1: current.t2, t2: current.t1 + current.t2 };
  }

  private requireChannel(): ChatChannel {
    if (!this.channel) {
      throw new Error('FollowUpChatService.configureForFollowUp() must be called before chat operations.');
    }
    return this.channel;
  }
}
